import React, { useState, useRef, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  LinearProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PhotoLibrary,
  Send,
  Close as CloseIcon,
} from '@mui/icons-material';
import { usePosts } from '../../context/posts.context';
import { useAuth } from '../../context/auth.context';
import { useSnackbar } from '../../context/snackbar.context';
import { getInitials } from '../../utils/get-initials.util';

const MAX_CHARS = 500;

const NewPostDialog = () => {
  const [postContent, setPostContent] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { createPost, submitting, newPostOpen, closeNewPost } = usePosts();
  const { profile } = useAuth();
  const { showSnackbar } = useSnackbar();

  const charCount = postContent.length;
  const isOverLimit = charCount > MAX_CHARS;
  const canSubmit = postContent.trim().length > 0 && !isOverLimit && !submitting;

  const handleContentChange = useCallback((e) => {
    setPostContent(e.target.value);
  }, []);

  const handleImageSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }

    setError(null);
    setImageFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
  }, []);

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setError(null);
    setUploadProgress(0);

    try {
      await createPost({
        type: 'text',
        text: postContent,
        imageFile: imageFile || undefined,
        onImageProgress: (progress) => setUploadProgress(progress),
      });
      setPostContent('');
      setImageFile(null);
      setImagePreview(null);
      setUploadProgress(0);
      closeNewPost();
      showSnackbar('Post created!', 'success');
    } catch (err) {
      setError(err.message || 'Failed to create post. Please try again.');
    }
  }, [canSubmit, postContent, imageFile, createPost, closeNewPost, showSnackbar]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setPostContent('');
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setError(null);
    closeNewPost();
  }, [submitting, closeNewPost]);

  return (
    <Dialog
      open={newPostOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      aria-labelledby="new-post-dialog-title"
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={profile?.avatarUrl || undefined}
            alt={`${profile?.displayName || 'Your'} avatar`}
            sx={{ bgcolor: 'background.paper', color: 'text.primary' }}
          >
            {getInitials(profile?.displayName || '')}
          </Avatar>
          <Typography variant="subtitle2" id="new-post-dialog-title">
            New Post
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Close dialog"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        {/* Text input */}
        <TextField
          onChange={handleContentChange}
          value={postContent}
          autoFocus
          margin="dense"
          id="post"
          label="What's happening?"
          type="text"
          fullWidth
          multiline
          rows={4}
          disabled={submitting}
          inputProps={{ maxLength: MAX_CHARS + 50 }}
        />

        {/* Character count */}
        <Box display="flex" justifyContent="flex-end" mt={0.5}>
          <Typography
            variant="caption"
            color={isOverLimit ? 'error' : charCount > MAX_CHARS * 0.9 ? 'warning.main' : 'text.secondary'}
          >
            {charCount}/{MAX_CHARS}
          </Typography>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Image preview */}
        {imagePreview && (
          <Box sx={{ mt: 2, position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
            <Box
              component="img"
              src={imagePreview}
              alt="Upload preview"
              sx={{
                width: '100%',
                maxHeight: 300,
                objectFit: 'cover',
                borderRadius: 2,
                display: 'block',
              }}
            />
            <IconButton
              onClick={handleRemoveImage}
              disabled={submitting}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
              }}
              size="small"
              aria-label="Remove image"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Upload progress */}
        {submitting && imageFile && uploadProgress > 0 && uploadProgress < 100 && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Uploading image... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Footer: actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Box display="flex" justifyContent="start" alignItems="center" gap={1}>
          <input
            ref={fileInputRef}
            hidden
            accept="image/jpeg,image/png,image/gif,image/webp"
            type="file"
            onChange={handleImageSelect}
          />
          <IconButton
            onClick={handleImageUploadClick}
            disabled={submitting}
            sx={{
              '&:hover': { color: 'primary.main' },
            }}
            color="inherit"
            aria-label="Upload image"
          >
            <PhotoLibrary />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant="contained"
            color="primary"
            endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
            sx={{ fontSize: '0.875rem', fontWeight: 700 }}
          >
            {submitting ? 'Posting...' : 'Post'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NewPostDialog;
